import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { SectionIntro } from "@/components/site/SectionHead";
import { Reveal, Stagger, StaggerItem } from "@/components/site/motion";
import { PillLink } from "@/components/site/Buttons";
import { SpotCard } from "@/components/site/ClipFrame";
import { RevealEmail } from "@/components/site/RevealEmail";
import { PartnerForm } from "@/components/forms/PartnerForm";
import { scrollToTarget } from "@/components/site/SmoothScroll";
import { PARTNER_GROUPS, PARTNER_STEPS } from "@/content/partners";
import { LINKS } from "@/content/site";

const ThinkingShipsAside = () => (
  <SpotCard className="p-7 sm:p-9" testId="partners-thinkingships">
    <p className="eyebrow text-fg-3">Operating ships today?</p>
    <p className="mt-4 text-2xl font-[520] leading-snug tracking-[-0.02em] text-fg">ThinkingShips is our product for shipowners and managers, available now.</p>
    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[15px]">
      <a href={LINKS.thinkingships} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-fg hover:text-signal" data-testid="partners-thinkingships-site">thinkingships.ai <ArrowUpRight className="h-4 w-4" /></a>
      <a href={LINKS.thinkingshipsDemo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-fg-2 hover:text-signal" data-testid="partners-thinkingships-demo">Try the product demo <ArrowUpRight className="h-4 w-4" /></a>
    </div>
  </SpotCard>
);

export default function Partners() {
  return (
    <div data-testid="partners-page">
      <PageHero
        testId="partners-hero"
        eyebrow="Partner with us"
        lines={["Solve part of", "the problem", "with us."]}
        text="No one company will build this alone. Where great technology already exists, we'd rather integrate it than reinvent it."
        image="/media/port-night.jpg"
        imageCaption="Ports, ships and the machines between them"
      >
        <Reveal delay={0.45} className="mt-10 flex flex-wrap gap-3">
          <PillLink onClick={() => scrollToTarget("#enquire")} testId="partners-enquire-cta">Start a conversation</PillLink>
        </Reveal>
      </PageHero>

      <section className="shell py-24 sm:py-32">
        <SectionIntro label="How we partner" title="Build with us." text="We build with teams who already solve part of the problem. Tell us where your technology fits." />
        <Stagger className="mt-14 grid gap-4 md:grid-cols-3">
          {PARTNER_STEPS.map((s) => (
            <StaggerItem key={s.k}>
              <SpotCard className="h-full p-7 sm:p-8" testId={`partner-step-${s.k}`}>
                <span className="mono text-[12px] text-signal">{s.k}</span>
                <h3 className="mt-10 text-2xl font-[540] tracking-[-0.025em] text-fg">{s.t}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-fg-2">{s.v}</p>
              </SpotCard>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="bg-ink-2 py-24 sm:py-32">
        <div className="shell">
          <SectionIntro label="Who we work with" title="If you build part of the ship, we should talk." text="One rule for everything we build: nothing outside the ship manager's control has to change. Ports, cranes and other vessels stay as they are." />
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 lg:grid-cols-4">
            {PARTNER_GROUPS.map((g) => (
              <StaggerItem key={g.k} className="bg-ink-2 p-7" data-testid={`partner-group-${g.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="text-lg font-[540] tracking-[-0.015em] text-fg">{g.k}</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-fg-2">{g.v}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section id="enquire" className="shell grid gap-14 py-24 sm:py-32 lg:grid-cols-12" data-testid="partner-enquiry-section">
        <div className="space-y-8 lg:col-span-5">
          <Reveal><p className="eyebrow text-signal">Enquiry</p></Reveal>
          <Reveal delay={0.05}><h2 className="text-[2.2rem] font-[540] leading-[1.02] tracking-[-0.035em] text-fg sm:text-5xl">Tell us what you've built.</h2></Reveal>
          <Reveal delay={0.1}><p className="text-lg leading-relaxed text-fg-2">A few lines are enough. Your message goes straight to the founders, and we reply personally.</p></Reveal>
          <Reveal delay={0.15}><RevealEmail testId="partners-reveal-email" /></Reveal>
          <Reveal delay={0.2}><ThinkingShipsAside /></Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="rounded-3xl border border-white/[0.08] bg-ink-2 p-6 sm:p-10"><PartnerForm /></div>
        </Reveal>
      </section>
    </div>
  );
}
